import { useState, useRef, useEffect } from 'react';
import { ReflectionSlideData, Theme, slideTypographyStyle } from '../slideTypes';

export function ReflectionSlide({
  data,
  theme,
  isEditing,
  onUpdate,
  isSubmittable,
  savedAnswers,
  onSave,
  onSubmit,
}: {
  data: ReflectionSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: ReflectionSlideData) => void;
  isSubmittable?: boolean;
  savedAnswers?: Record<string, string>;
  onSave?: (answers: Record<string, string>) => void;
  onSubmit?: (answers: Record<string, string>) => void;
}) {
  // Answer state for viewer mode
  const [answers, setAnswers] = useState<Record<string, string>>(savedAnswers || {});
  const [submitted, setSubmitted] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-save on answer change (debounced)
  const handleAnswerChange = (questionId: string, value: string) => {
    const updated = { ...answers, [questionId]: value };
    setAnswers(updated);
    if (onSave) {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => onSave(updated), 1500);
    }
  };

  // Clean up timer on unmount
  useEffect(() => {
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, []);

  // Submit to instructor
  const handleSubmit = () => {
    // Save immediately before submitting
    if (onSave) onSave(answers);
    if (onSubmit) {
      onSubmit(answers);
      setSubmitted(true);
    }
  };

  const addQuestion = (afterIndex?: number) => {
    const newQ = { id: crypto.randomUUID(), prompt: '', placeholder: 'Share your thoughts...' };
    if (afterIndex !== undefined) {
      const updated = [...data.questions];
      updated.splice(afterIndex + 1, 0, newQ);
      onUpdate({ ...data, questions: updated });
    } else {
      onUpdate({ ...data, questions: [...data.questions, newQ] });
    }
    setTimeout(() => {
      const inputs = document.querySelectorAll<HTMLInputElement>('[data-reflection-prompt]');
      const idx = afterIndex !== undefined ? afterIndex + 1 : inputs.length - 1;
      inputs[idx]?.focus();
    }, 50);
  };

  const updateQuestion = (id: string, updates: Partial<{ prompt: string; placeholder: string }>) => {
    onUpdate({
      ...data,
      questions: data.questions.map((q) => (q.id === id ? { ...q, ...updates } : q)),
    });
  };

  const removeQuestion = (id: string) => {
    const index = data.questions.findIndex((q) => q.id === id);
    onUpdate({ ...data, questions: data.questions.filter((q) => q.id !== id) });
    setTimeout(() => {
      const inputs = document.querySelectorAll<HTMLInputElement>('[data-reflection-prompt]');
      const idx = Math.max(0, index - 1);
      inputs[idx]?.focus();
    }, 50);
  };

  const handleKeyDown = (e: React.KeyboardEvent, questionId: string, index: number) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      addQuestion(index);
    } else if (e.key === 'Backspace' && data.questions[index].prompt === '') {
      e.preventDefault();
      if (data.questions.length > 1) removeQuestion(questionId);
    }
  };

  return (
    <div className="h-full flex flex-col justify-center px-[8%] py-[6%]">
      {isEditing ? (
        <input
          type="text"
          value={data.heading || ''}
          onChange={(e) => onUpdate({ ...data, heading: e.target.value })}
          placeholder="Reflection heading (optional)..."
          className="w-full mb-4 bg-transparent border-0 border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none transition-colors"
          style={{
            ...slideTypographyStyle(theme, 'heading'),
            color: theme.colors.text,
          }}
        />
      ) : (
        data.heading && (
          <h2
            className="mb-4"
            data-slide-level="heading"
            style={{
              ...slideTypographyStyle(theme, 'heading'),
              color: theme.colors.text,
            }}
          >
            {data.heading}
          </h2>
        )
      )}

      {isEditing ? (
        <input
          type="text"
          value={data.description || ''}
          onChange={(e) => onUpdate({ ...data, description: e.target.value })}
          placeholder="Brief description or context (optional)..."
          className="w-full mb-6 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none transition-colors text-sm"
          style={{ color: theme.colors.textMuted }}
        />
      ) : (
        data.description && (
          <p className="mb-6 text-sm" style={{ color: theme.colors.textMuted }}>
            {data.description}
          </p>
        )
      )}

      <div className="space-y-4">
        {data.questions.map((question, index) => (
          <div key={question.id} className="flex items-start gap-3">
            <span
              className="mt-2.5 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
              style={{ backgroundColor: theme.colors.primary + '20', color: theme.colors.primary }}
            >
              {index + 1}
            </span>
            <div className="flex-1 space-y-2">
              {isEditing ? (
                <>
                  <input
                    type="text"
                    data-reflection-prompt
                    value={question.prompt}
                    onChange={(e) => updateQuestion(question.id, { prompt: e.target.value })}
                    onKeyDown={(e) => handleKeyDown(e, question.id, index)}
                    placeholder={`Question ${index + 1}... (Enter for new, Backspace to remove empty)`}
                    className="w-full bg-transparent border-0 border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none transition-colors font-medium"
                    style={{ color: theme.colors.text }}
                  />
                  <input
                    type="text"
                    value={question.placeholder || ''}
                    onChange={(e) => updateQuestion(question.id, { placeholder: e.target.value })}
                    placeholder="Placeholder hint..."
                    className="w-full bg-transparent border-0 border-b border-transparent hover:border-gray-200 focus:border-blue-400 focus:outline-none transition-colors text-xs"
                    style={{ color: theme.colors.textMuted }}
                  />
                </>
              ) : (
                <>
                  <p className="font-medium" style={{ color: theme.colors.text }}>
                    {question.prompt}
                  </p>
                  <textarea
                    value={answers[question.id] || ''}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    placeholder={question.placeholder || 'Share your thoughts...'}
                    className="w-full p-3 rounded-lg border resize-none focus:outline-none focus:ring-2 min-h-[80px]"
                    style={{
                      backgroundColor: theme.colors.surface,
                      borderColor: theme.colors.textMuted + '30',
                      color: theme.colors.text,
                    }}
                  />
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Submit button for submittable reflection slides (viewer mode only) */}
      {!isEditing && isSubmittable && onSubmit && (
        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={handleSubmit}
            disabled={submitted || Object.values(answers).every(v => !v.trim())}
            className="px-5 py-2.5 text-sm font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: submitted ? '#059669' : theme.colors.primary,
              color: 'white',
            }}
          >
            {submitted ? '\u2713 Submitted' : 'Submit to Instructor'}
          </button>
          {submitted && (
            <span className="text-xs" style={{ color: theme.colors.textMuted }}>
              Your response has been sent for review
            </span>
          )}
        </div>
      )}

      {isEditing && (
        <button
          onClick={() => addQuestion()}
          className="mt-4 px-4 py-2 text-sm font-medium rounded-lg transition-colors"
          style={{
            backgroundColor: theme.colors.primary + '15',
            color: theme.colors.primary,
          }}
        >
          + Add Question
        </button>
      )}

      {isEditing && data.questions.length === 0 && (
        <div className="mt-4 p-6 border-2 border-dashed border-gray-200 rounded-xl text-center">
          <p className="text-sm text-gray-500 mb-2">No reflection questions yet</p>
          <button
            onClick={() => addQuestion()}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
          >
            Add First Question
          </button>
        </div>
      )}
    </div>
  );
}
